import { Stack, Typography } from '@mui/material';
import { forgotPassword } from 'API/user';
import { ErrorMessages, Routes } from 'enums';
import PaiLink from 'features/ui/base-components/pai-link';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useErrorStore from 'stores/error-store';
import { Shadows } from 'theme';
import { makeStyles } from 'tss-react/mui';
import isEmail from 'validator/lib/isEmail';
import PaiButton from '../features/ui/base-components/pai-button';
import PaiTextField from '../features/ui/base-components/pai-text-field';

const ResetPasswordRequest: React.FC = () => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleOpenErrorDialog = useErrorStore((state) => state.handleOpenErrorDialog);

  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailHelperText, setEmailHelperText] = useState('');

  useEffect(() => setEmailHelperText(''), [emailInput]);

  const handleEmailInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(event.target.value);
  };

  const isValid = () => {
    return isEmail(emailInput);
  };

  const handleSendEmail = async () => {
    if (!isValid()) {
      setEmailHelperText('* ' + t('invalidEmail'));
      return;
    }
    setLoading(true);
    const res = await forgotPassword({
      email: emailInput,
    });

    if (res && !res.error) {
      setEmailSent(true);
    } else {
      const validationErrorMessage = (res as any).error?.errors?.body?.[0]?.message;
      if (res.error.code === 400 && res.error.message === ErrorMessages.USER_NOT_EXISTS) {
        setEmailHelperText('* ' + t('userNotFound'));
      } else if (res.error.code === 400 && validationErrorMessage === ErrorMessages.EMAIL_VALIDATION_ERROR) {
        setEmailHelperText('* ' + t('invalidEmail'));
      } else {
        handleOpenErrorDialog(res?.error, '/auth/forgot-password', 'post');
      }
    }
    setLoading(false);
  };

  return (
    <div className={classes.resetPasswordRequest}>
      <div className={classes.card}>
        <div className={classes.cardHeader}>
          <span className={classes.headerMark} />
          <Typography className={classes.headerTitle}>{t('authResetPasswordTitle')}</Typography>
        </div>
        <div className={classes.content}>
          <Stack gap={2.5}>
            <Typography className={classes.intro}>{t('authResetPasswordIntro')}</Typography>
            <div className={classes.field}>
              <Typography className={classes.fieldLabel}>{t('authFieldEmailLabel')}</Typography>
              <PaiTextField
                placeholder={t('authFieldEmailPlaceholder')}
                value={emailInput}
                onChange={handleEmailInputChange}
                error={emailHelperText !== ''}
                errorText={emailHelperText}
              />
            </div>
            <PaiButton
              text={t('requestPwMail')}
              action={handleSendEmail}
              size={'large'}
              fullWidth={true}
              loading={loading}
              disabled={emailInput.trim() === '' || emailHelperText !== ''}
              style={{ height: '48px', borderRadius: '8px', fontSize: '1rem' }}
            />
            {emailSent ? (
              <Stack gap={1}>
                <Typography className={cx(classes.text, 'highlight-2')}>{t('forgetPasswordEmailSent1', { email: emailInput })}</Typography>
                <Typography className={cx(classes.text, 'highlight-1')}>{t('forgetPasswordEmailSent2')}</Typography>
              </Stack>
            ) : null}
            <Stack direction={'row'} justifyContent={'center'}>
              <PaiLink text={t('backToLogin')} size={'sm'} variant={'primary'} underline={'always'} action={() => navigate(Routes.SIGNIN)} />
            </Stack>
          </Stack>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  resetPasswordRequest: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  card: {
    width: '100%',
    maxWidth: '440px',
    background: '#FFF',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: Shadows.GREY_SHADOW_LIGHTER,
    [theme.breakpoints.down('sm')]: {
      boxShadow: 'unset',
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '24px 28px',
    borderBottom: '1px solid #F0F0F5',
  },
  headerMark: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    flexShrink: 0,
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
  headerTitle: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
  },
  content: {
    padding: '28px',
  },
  intro: {
    fontSize: theme.typography.pxToRem(14),
    color: '#555',
    lineHeight: 1.6,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    '& .MuiFormControl-root': {
      borderColor: '#E3E3E8',
      borderRadius: '8px',
    },
    '& .MuiInputBase-root': {
      borderRadius: '8px',
    },
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      minHeight: 'calc(44px - 16px)',
    },
    '& .MuiFormControl-root:focus-within': {
      borderColor: theme.palette.primary.main,
    },
    '& .MuiFormControl-root:has(.Mui-error)': {
      borderColor: theme.palette.error.main,
    },
  },
  fieldLabel: {
    fontSize: theme.typography.pxToRem(13),
    color: '#707070',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
    whiteSpace: 'pre-wrap',
    '&.highlight-1': {
      fontWeight: 'bold',
      textDecoration: 'underline',
      textAlign: 'center',
    },
    '&.highlight-2': {
      color: theme.palette.greys.medium,
    },
  },
}));

export default ResetPasswordRequest;
